import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppSelector, useAppDispatch } from "../redux/hooks";
import {
  selectCurrentAccessToken,
  selectCurrentRefreshToken,
  setCredentials,
} from "../redux/auth/authSlice";
import {
  selectExpiryTimeInMinute,
  selectWarningAlertTimeoutInMinute,
} from "../redux/config/configSlice";
import { useRefreshSessionMutation } from "../redux/auth/authApiSlice";
import useAuth from "./useAuth";

interface UseIdleTimeoutReturn {
  showWarning: boolean;
  remainingTime: number;
  resetTimer: () => void;
  handleLogout: () => void;
}

const WARNING_TIME_SEC = 30; // 30 seconds warning before session expiry

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "click",
];

const useIdleTimeout = (): UseIdleTimeoutReturn => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { logout } = useAuth();

  const jwtToken = useAppSelector(selectCurrentAccessToken);
  const refreshTokenValue = useAppSelector(selectCurrentRefreshToken);
  const expiryTimeMin = useAppSelector(selectExpiryTimeInMinute);
  const warningTimeMin = useAppSelector(selectWarningAlertTimeoutInMinute);

  const expiryTimeSec = expiryTimeMin * 60;
  const warningTimeSec = warningTimeMin * 60 || WARNING_TIME_SEC;

  const [showWarning, setShowWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(warningTimeSec);

  const [refreshSessionApi] = useRefreshSessionMutation();

  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasLoggedOutRef = useRef(false);
  const showWarningRef = useRef(false);

  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const handleLogout = useCallback(async () => {
    if (hasLoggedOutRef.current) return;
    hasLoggedOutRef.current = true;

    clearAllTimers();
    setShowWarning(false);
    showWarningRef.current = false;

    toast.dismiss();
    toast.error("Your session has expired due to inactivity. Please login again.");
    await logout({ isSessionTimeout: true });
    navigate("/login", { replace: true });
  }, [clearAllTimers, logout, navigate]);

  const startCountdown = useCallback((countdownSec: number) => {
    setShowWarning(true);
    showWarningRef.current = true;
    setRemainingTime(Math.round(countdownSec));

    const logoutTime = Date.now() + countdownSec * 1000;

    countdownIntervalRef.current = setInterval(() => {
      const remaining = Math.max(0, Math.round((logoutTime - Date.now()) / 1000));
      setRemainingTime(remaining);
      if (remaining <= 0 && countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    }, 1000);

    logoutTimerRef.current = setTimeout(() => {
      handleLogout();
    }, countdownSec * 1000);
  }, [handleLogout]);

  const startIdleTimer = useCallback(() => {
    clearAllTimers();
    hasLoggedOutRef.current = false;
    setShowWarning(false);
    showWarningRef.current = false;
    setRemainingTime(warningTimeSec);

    const timeUntilWarningSec = expiryTimeSec - warningTimeSec;

    if (timeUntilWarningSec <= 0) {
      startCountdown(expiryTimeSec);
      return;
    }

    warningTimerRef.current = setTimeout(() => {
      startCountdown(warningTimeSec);
    }, timeUntilWarningSec * 1000);
  }, [clearAllTimers, expiryTimeSec, warningTimeSec, startCountdown]);

  // "Stay Logged In" - refresh the token and restart the idle timer
  const resetTimer = useCallback(async () => {
    clearAllTimers();
    setShowWarning(false);
    showWarningRef.current = false;
    setRemainingTime(warningTimeSec);

    try {
      const response: any = await refreshSessionApi({
        accessToken: jwtToken,
        refreshToken: refreshTokenValue,
      }).unwrap();
      dispatch(setCredentials(response.result));
      // Restart idle timer after successful refresh
      startIdleTimer();
    } catch {
      // If refresh fails, force logout
      handleLogout();
    }
  }, [clearAllTimers, warningTimeSec, refreshSessionApi, jwtToken, refreshTokenValue, dispatch, handleLogout, startIdleTimer]);

  // Start idle timer on mount and whenever token changes
  useEffect(() => {
    if (!jwtToken) return;

    startIdleTimer();

    return () => {
      clearAllTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwtToken, expiryTimeSec, warningTimeSec]);

  // Listen for user activity and reset idle timer (only when warning is NOT showing)
  useEffect(() => {
    if (!jwtToken) return;

    const handleUserActivity = () => {
      // Don't reset if warning dialog is already showing or user has logged out
      if (showWarningRef.current || hasLoggedOutRef.current) return;
      startIdleTimer();
    };

    ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, handleUserActivity, { passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, handleUserActivity);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwtToken, startIdleTimer]);

  return {
    showWarning,
    remainingTime,
    resetTimer,
    handleLogout,
  };
};

export default useIdleTimeout;
